import { useAuthContext } from "@/features/authentication/useAuthContext";
import type { Recipe } from "@/features/Recipes-renamed/types";
import { mapDatabaseRecipeToRecipe, type RecipesRowWithRelations } from "@/lib/mapDatabaseRecipeToRecipe";
import { supabase } from "@/lib/supabase";
import { RecipeType, recipeTypeFromPath } from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useGetRecipeTypes, type RecipeTypeRow } from "./useGetRecipeTypes";
import { useGetAppliedFiltersFromLocation } from "./useGetAppliedFiltersFromLocation";
import { useGetRecipeCategories } from "./useGetRecipeCategories";

function getRecipeTypeIdFromType(recipeType: RecipeType, recipeTypes: RecipeTypeRow[]): number | undefined {
    console.log("in function");
    const type = recipeTypes.find((t) => t.name === recipeType);
    return type ? type.id : undefined;
}

export function useGetRecipes(isType?: boolean) {
    const { user } = useAuthContext();
    const { data: recipeTypeRows } = useGetRecipeTypes();
    const { data: recipeCategories } = useGetRecipeCategories();
    const appliedFilters = useGetAppliedFiltersFromLocation();
    const location = useLocation();
    let recipeTypeId: number | undefined = undefined;
    if (isType) {
        const recipeType = recipeTypeFromPath(location.pathname);
        if (recipeType && recipeTypeRows) {
            recipeTypeId = getRecipeTypeIdFromType(recipeType, recipeTypeRows);
        }
    }

    const isQueryEnabled =
        !!user &&
        (!isType || (isType && recipeTypeId !== undefined)) &&
        (!appliedFilters.categories.length || (appliedFilters.categories.length > 0 && !!recipeCategories));

    return useQuery<Recipe[]>({
        enabled: isQueryEnabled,
        queryKey: ["recipes", recipeTypeId ?? null, appliedFilters],
        queryFn: async () => {
            let selectStatement = `
                    *,
                    recipe_types (*),
                    recipe_categories (*),
                    ingredient_groups (*, ingredients (*))
                `;

            if (appliedFilters.categories.length > 0) {
                selectStatement = `
                    *,
                    recipe_types (*),
                    recipe_categories!inner(*),
                    ingredient_groups (*, ingredients (*))
                `;
            }

            let query = supabase
                .from("recipes")
                .select(selectStatement)
                .eq("user_id", user!.id)
                .order("created_at", { ascending: false });

            if (isType && recipeTypeId !== undefined) {
                query = query.eq("recipe_type_id", recipeTypeId);
            }

            if (appliedFilters.query) {
                query = query.ilike("name", `%${appliedFilters.query}%`);
            }

            if (appliedFilters.hasMadeIt) {
                const hasMadeIt = appliedFilters.hasMadeIt === "true";
                query = query.eq("has_made_it", hasMadeIt);
            }

            if (appliedFilters.isFavorite) {
                query = query.eq("favorite", true);
            }

            if (appliedFilters.types.length > 0) {
                const typeIds = appliedFilters.types
                    .map((rType) => getRecipeTypeIdFromType(rType as RecipeType, recipeTypeRows || []))
                    .filter((id): id is number => id !== undefined);
                if (typeIds.length > 0) {
                    query = query.in("recipe_type_id", typeIds);
                }
            }

            if (appliedFilters.categories.length > 0) {
                const categoryIds = appliedFilters.categories
                    .map((categoryName) => {
                        const category = recipeCategories?.find((cat) => cat.name === categoryName);
                        return category ? category.id : undefined;
                    })
                    .filter((id): id is number => id !== undefined);
                if (categoryIds.length > 0) {
                    query = query.in("recipe_categories.id", categoryIds);
                }
            }

            const { data, error } = await query;

            if (error || !data) {
                console.error("Error fetching recipes:", error);
                throw error;
            }

            const recipes: Recipe[] = data.map((rows) =>
                mapDatabaseRecipeToRecipe(rows as unknown as RecipesRowWithRelations)
            );

            return recipes;
        }
    });
}
